<template>
  <v-container fluid class="hyp-table-container">
    <v-row dense>
      <v-col cols="auto" class="pa-0 ma-0">
        <v-btn
          color="primary"
          outlined
          small
          v-on:click="createIncident"
          class="pull-left mr-5"
          v-show="hasMyIncidentsEditor"
        >
          {{ labelAddNew }}
        </v-btn>
      </v-col>
      <v-col v-if="!isMapControlsFeatureUnlocked" cols="auto" class="pa-0 ma-0">
        <v-switch
          v-model="myIncidentsMapEnabled"
          solo
          class="pull-right pa-0 ma-0"
          label="View on map"
          v-on:change="handleChangeMyIncidentsMapEnabled"
        ></v-switch>
      </v-col>
    </v-row>

    <v-row dense class="pa-0 ma-0">
      <v-col cols="2" class="pa-0 mr-5">
        <v-text-field
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          clearable
          @input="debounceSearch"
        ></v-text-field>
      </v-col>
      <v-col cols="3" class="pt-4 ma-0">
        <DateRangePicker
          :start="options.startDate"
          :end="options.endDate"
          size="small"
          class="pull-left"
          @change="handleChangeDate"
        ></DateRangePicker>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="hyp-data-table-container">
        <v-data-table
          dense
          v-resize="handleResize"
          :headers="headers"
          :items="incidents.incidents"
          :items-per-page="itemsPerPage"
          :server-items-length="totalIncidents"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          :height="listingHeight"
          fixed-header
          :loading="loading"
          :loading-text="labelLoading"
          class="my-incidents-data-table"
          @update:options="handleTableOptions"
          :footer-props="{ 'items-per-page-options': [20, 40, 60, 100] }"
          multi-sort
        >
          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon small class="mr-2" @click="flyToItem(item)">
                    mdi-earth
                  </v-icon>
                </span>
              </template>
              <span
                >{{ item.location.latitude }},
                {{ item.location.longitude }}</span
              >
            </v-tooltip>
            <v-icon
              small
              class="mr-2"
              v-show="hasMyIncidentsEditor"
              @click="editIncident(item)"
            >
              mdi-pencil
            </v-icon>
            <v-icon
              small
              class="mr-2"
              v-show="hasMyIncidentsEditor"
              @click="deleteIncidentDialog(item)"
            >
              mdi-delete
            </v-icon>
          </template>
          <template v-slot:[`item.incidentDate`]="{ item }">
            <span>{{ new Date(item.incidentDate).toLocaleDateString() }}</span>
          </template>
          <template v-slot:[`item.incidentTime`]="{ item }">
            <span>{{ formatIncidentTime(item) }}</span>
          </template>
          <template v-slot:[`item.createdDate`]="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{
                  new Date(item.createdDate).toLocaleDateString()
                }}</span>
              </template>
              <span>{{ new Date(item.createdDate).toLocaleString() }}</span>
            </v-tooltip>
          </template>
          <template v-slot:[`item.updatedDate`]="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{
                  new Date(item.updatedDate).toLocaleDateString()
                }}</span>
              </template>
              <span>{{ new Date(item.updatedDate).toLocaleString() }}</span>
            </v-tooltip>
          </template>
          <template v-slot:no-data>
            <div class="my-5">
              There are currently no incidents. <br /><br />
            </div>
          </template>
          <template v-slot:no-results>
            <div class="my-5">
              Your search returned no results.
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <MyIncidentDialog
      :show="createDialog"
      @close="handleDialogClose"
    ></MyIncidentDialog>
    <v-dialog v-model="dialogDelete" max-width="500px">
      <v-card>
        <v-card-title>
          <span>{{ deleteIncident.title }}</span>
        </v-card-title>
        <v-card-text class="text-center">
          <span>Are you sure you want to delete this incident?</span>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="default" text @click="closeDelete">Cancel</v-btn>
          <v-btn
            :disabled="deleting"
            class="primary"
            text
            @click="deleteIncidentConfirmed"
          >
            Delete
            <v-progress-circular
              v-if="deleting"
              indeterminate
              size="14"
              width="2"
              class="ml-2"
            ></v-progress-circular>
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashDebounce from "lodash/debounce";

import { LABELS } from "@/common/constants/labels";
import {
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_MY_INCIDENTS,
  MODULE_PREFIX_TIME,
  MODULE_PREFIX_USER,
} from "@/store";
import {
  DELETE_MY_INCIDENT,
  GET_MY_INCIDENTS,
  GET_MY_INCIDENTS_PROFILE,
  GET_MY_INCIDENTS_REFRESH_MAP,
  SAVE_PREFERENCES,
  GET_MY_INCIDENTS_FILTERS,
} from "@/store/actions.type";
import { mapGetters } from "vuex";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { hasMyIncidentsEditorAccess } from "@/services/user/user.service";
import dayjs from "dayjs";

const headerOptionValues = {
  TITLE: "title",
  CATEGORY: "category",
  DESCRIPTION: "description",
  INCIDENT_DATE: "incidentDate",
  COUNTRY: "country",
  CREATED_DATE: "createdDate",
  UPDATED_DATE: "updatedDate",
  INCIDENT_TIME: "incidentTime",
  SEVERITY: "severity",
};

const sortDir = {
  ASCENDING: "asc",
  DESCENDING: "desc",
};

export default {
  name: "MyIncidentsTable",
  components: {
    DateRangePicker: () => import("@/components/common/DateRangePicker.vue"),
    MyIncidentDialog: () =>
      import("@/components/myIncidents/MyIncidentDialog.vue"),
  },
  data: function () {
    return {
      validForm: false,
      defaultHeaders: [
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: 125,
        },
        {
          text: "Title",
          value: headerOptionValues.TITLE,
          sortable: true,
          width: 200,
        },
        {
          text: "Category",
          value: headerOptionValues.CATEGORY,
          sortable: true,
          width: 100,
        },
        {
          text: "Date",
          value: headerOptionValues.INCIDENT_DATE,
          sortable: true,
          width: 75,
        },
        {
          text: "Time (UTC)",
          value: headerOptionValues.INCIDENT_TIME,
          sortable: true,
          width: 150,
        },
        {
          text: "Country",
          value: headerOptionValues.COUNTRY,
          sortable: true,
          width: 100,
        },
        {
          text: "Description",
          value: headerOptionValues.DESCRIPTION,
          sortable: true,
          width: 250,
        },
        {
          text: "Severity",
          value: headerOptionValues.SEVERITY,
          sortable: true,
          width: 100,
        },
        {
          text: "Created",
          value: headerOptionValues.CREATED_DATE,
          sortable: true,
          width: 125,
        },
        {
          text: "Updated",
          value: headerOptionValues.UPDATED_DATE,
          sortable: true,
          width: 125,
        },
      ],
      customFieldHeaders: [],
      dialogDelete: false,
      deleting: false,
      deleteIncident: {
        id: undefined,
        title: undefined,
      },
      hasMyIncidentsEditor: hasMyIncidentsEditorAccess(),
      labelAddNew: `Add New ${LABELS.myIncident}`,
      labelLoading: `Retrieving ${LABELS.myIncidents}`,
      labelName: `${LABELS.myIncidents} name`,
      loading: false,
      windowSize: {
        x: 0,
        y: 0,
      },
      createDialog: { show: false },
      menu: false,
      myIncidentsMapEnabled: false,
      options: {},
      pageNum: 1,
      itemsPerPage: 20,
      totalIncidents: 0,
      sortBy: [headerOptionValues.CREATED_DATE],
      sortDesc: [true],
      search: "",
    };
  },
  async mounted() {
    await this.refresh();
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      utcDate: MODULE_PREFIX_TIME + "today",
      preferences: MODULE_PREFIX_USER + "preferences",
      incidents: MODULE_PREFIX_MY_INCIDENTS + "incidents",
      profile: MODULE_PREFIX_MY_INCIDENTS + "profile",
    }),
    listingHeight() {
      return this.windowSize.y - 350;
    },
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    headers() {
      const hiddenHeaders = [].concat(
        this.profile?.incidentTimeEnabled
          ? []
          : [headerOptionValues.INCIDENT_TIME],
        this.profile?.severityEnabled ? [] : [headerOptionValues.SEVERITY]
      );
      return []
        .concat(this.defaultHeaders, this.customFieldHeaders)
        .filter(({ value }) => !hiddenHeaders.includes(value));
    },
    isMapControlsFeatureUnlocked() {
      return this.featureUnlocked("map-controls");
    },
  },
  methods: {
    async getMyIncidents() {
      this.loading = true;
      await this.$store.dispatch(
        MODULE_PREFIX_MY_INCIDENTS + GET_MY_INCIDENTS,
        {
          options: this.options,
        }
      );
      this.pageNum = this.incidents.metadata.pageNumber;
      this.itemsPerPage = this.incidents.metadata.count;
      this.totalIncidents = this.incidents.metadata.total;
    },
    handleResize() {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight };
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.reset();
      });
    },
    reset() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },
    handleChangeMyIncidentsMapEnabled() {
      this.savePrefs();
      this.refreshMap();
    },
    async handleDialogClose(event) {
      this.createDialog = { show: false };
      if (event.refresh) {
        await this.getMyIncidents();
      }
    },
    flyToItem(item) {
      this.map.flyTo({
        center: [item.location.longitude, item.location.latitude],
        zoom: 7,
      });
    },
    editIncident(item) {
      this.createDialog = { show: true, incident: lodashCloneDeep(item) };
    },
    closeDelete() {
      this.deleteIncident = { id: undefined, title: undefined };
      this.dialogDelete = false;
    },
    deleteIncidentDialog(item) {
      this.deleteIncident = { id: item.id, title: item.title };
      this.dialogDelete = true;
    },
    async deleteIncidentConfirmed() {
      this.deleting = true;
      await this.$store.dispatch(
        MODULE_PREFIX_MY_INCIDENTS + DELETE_MY_INCIDENT,
        this.deleteIncident.id
      );
      this.closeDelete();
      this.deleting = false;
      await this.getMyIncidents();
    },
    savePrefs() {
      let preferences = lodashCloneDeep(this.preferences);
      preferences.myIncidents = preferences.myIncidents || {};
      preferences.myIncidents.enabled = this.myIncidentsMapEnabled;
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
      preferences = null;
    },
    async handleTableOptions(event) {
      const { page, itemsPerPage, sortBy, sortDesc } = event;
      this.options = {
        ...this.options,
        page: page,
        itemsPerPage: itemsPerPage,
        sortBy: sortBy,
        sortDirection: sortDesc.map((d) =>
          d ? sortDir.DESCENDING : sortDir.ASCENDING
        ),
      };
    },
    async debounceSearch(event) {
      lodashDebounce(async () => {
        this.options = {
          ...this.options,
          search: event,
        };
        this.$store.dispatch(
          MODULE_PREFIX_MY_INCIDENTS + GET_MY_INCIDENTS_FILTERS,
          {
            timestamp: new Date().toISOString(),
            search: event,
            startDate: this.options.startDate,
            endDate: this.options.endDate,
          }
        );
      }, 750)();
    },
    async handleChangeDate(event) {
      this.options = {
        ...this.options,
        startDate: event.start,
        endDate: event.end,
      };
      this.$store.dispatch(
        MODULE_PREFIX_MY_INCIDENTS + GET_MY_INCIDENTS_FILTERS,
        {
          timestamp: new Date().toISOString(),
          search: this.options.search,
          startDate: event.start,
          endDate: event.end,
        }
      );
    },
    async refreshMap() {
      await this.$store.dispatch(
        MODULE_PREFIX_MY_INCIDENTS + GET_MY_INCIDENTS_REFRESH_MAP,
        {
          enabled: this.myIncidentsMapEnabled,
          search: this.options.search,
          startDate: this.options.startDate,
          endDate: this.options.endDate,
        }
      );
    },
    createIncident() {
      this.createDialog = { show: true };
    },
    async refresh() {
      await this.$store.dispatch(
        MODULE_PREFIX_MY_INCIDENTS + GET_MY_INCIDENTS_PROFILE,
        {}
      );
      this.populateCustomFieldHeaders();
      await this.getMyIncidents();
    },
    populateCustomFieldHeaders() {
      this.customFieldHeaders = Object.entries(this.profile?.customFields ?? {})
        .filter(([_, enabled]) => enabled)
        .map(([name, _]) => {
          return {
            text: name,
            value: `customFields.${name}`,
            sortable: false,
            width: 200,
          };
        });
    },
    formatIncidentTime(item) {
      if (!item.incidentTime) {
        return undefined;
      }
      return dayjs(`${item.incidentDate}T${item.incidentTime}`).format("HH:mm");
    },
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    "preferences.myIncidents": {
      handler() {
        this.myIncidentsMapEnabled = lodashGet(
          this.preferences,
          "myIncidents.enabled"
        );
      },
      immediate: true,
    },
    options: {
      async handler(val) {
        lodashDebounce(async () => {
          this.loading = true;
          await this.$store.dispatch(
            MODULE_PREFIX_MY_INCIDENTS + GET_MY_INCIDENTS,
            {
              options: val,
            }
          );
          await this.refreshMap();
        }, 500)();
      },
      deep: true,
    },
    incidents: {
      handler(val) {
        this.loading = false;
        this.pageNum = val.metadata.pageNumber;
        this.itemsPerPage = val.metadata.count;
        this.totalIncidents = val.metadata.total;
      },
    },
  },
};
</script>

<style scoped>
.hyp-table-container {
  width: 1200px;
}

.my-incidents-data-table {
  overflow-y: auto;
}

.v-data-table {
  overflow-x: auto;
}
</style>
